import { Button, Checkbox, Col, DatePicker, Form, Grid, Input, message, Modal, Row, Select } from 'antd';
import React, { useState } from 'react';
import '../../../../css/entity-style.css';
import dayjs from 'dayjs';
import { GENDERS } from '../../../../constants';
import { PhotoUpload } from '../../../shared/photo-upload/photo-upload';
import Title from 'antd/es/typography/Title';
import { GenerationalSuffix } from './report-cover-letter.container';
import { Entity } from '../../../../generated';
import { Country } from '../../../../types/country';
import { PageHeader } from '@ant-design/pro-layout';

interface ReportCoverLetterProps {
  generatePdf: (formData: ReportCoverLetterData) => Promise<File>;
  generationalSuffixOptions: GenerationalSuffix[];
  files: string[];
  sendDestinationEntities: Entity[];
  issuingInstitutionEntities: Entity[];
  countries: Country[];
}

export interface ReportCoverLetterData {
  applicantId: string;
  caseId: string;
  restOfName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  gender: string;
  photo: string;
  nameOnCredential: string;
  generationalSuffix: string;
  typeOfCredential: string;
  statusOfCredential: string;
  sendDestination: string;
  includedFiles: string[];
  issuingInstitution: string;
}

export const ReportCoverLetter: React.FC<ReportCoverLetterProps> = (props) => {
  const DATE_FORMAT = 'D MMMM YYYY';
  const MODAL_BODY_PADDING_TOP = '22px'; // add padding to body of modal to unhide the close button
  const screens = Grid.useBreakpoint();
  const isMobile = screens.xs;
  const [form] = Form.useForm<ReportCoverLetterData>();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [selectedFiles, setSelectedFiles] = useState<string[]>([props.files[0]]);
  const [emailDisabled, setEmailDisabled] = useState(false);
  const [applicantIdDisabled, setApplicantIdDisabled] = useState(false);
  const [isOtherInstitution, setIsOtherInstitution] = useState(false);

  const handleValuesChange: (changedValues: Partial<ReportCoverLetterData>) => void = (changedValues) => {
    if ('applicantId' in changedValues) {
      setEmailDisabled(!!changedValues.applicantId);
    }
    if ('email' in changedValues) {
      setApplicantIdDisabled(!!changedValues.email);
    }

    if ('issuingInstitution' in changedValues) {
      setIsOtherInstitution(changedValues.issuingInstitution === 'other');
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedFiles([props.files[0]]);
    form.resetFields();
    URL.revokeObjectURL(pdfUrl);
  };

  const handleCheck = (file: string, checked: boolean) => {
    console.log('test');
    if (checked) {
      setSelectedFiles((prev) => [...prev, file]); // Add to selected files
    } else {
      setSelectedFiles((prev) => prev.filter((item) => item !== file)); // Remove from selected files
    }
  };

  const onSubmit = async (values: any) => {
    try {
      const photoFile = values.photo.originFileObj;
      const readFile = (file: File) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = (error) => reject(error);
          reader.readAsDataURL(file);
        });
      const photoData = await readFile(photoFile);

      const coverLetterObj: ReportCoverLetterData = {
        applicantId: values.applicantId,
        caseId: values.caseId,
        restOfName: values.firstName,
        lastName: values.lastName,
        dateOfBirth: dayjs(values.dateOfBirth).format(DATE_FORMAT),
        email: values.email,
        gender: values.gender,
        photo: photoData,
        nameOnCredential: values.nameOnDocument,
        generationalSuffix: values.generationalSuffix,
        typeOfCredential: values.typeOfCredential,
        statusOfCredential: values.statusOfCredential,
        includedFiles: selectedFiles,
        sendDestination: values.issuedTo,
        issuingInstitution: values.issuingInstitutionName || values.issuingInstitution,
      };

      localStorage.setItem('coverLetterData', JSON.stringify(coverLetterObj)); // Set new data
      message.success('Form submitted successfully!');
      const generatedPdf = await props.generatePdf(coverLetterObj);
      const pdfBlob = new Blob([generatedPdf], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPdfUrl(pdfUrl);
      setIsModalVisible(true);
      setEmailDisabled(false);
      setApplicantIdDisabled(false);
    } catch (error: any) {
      message.error(`Form submission failed: ${error.message || 'Please check your input and try again'}`);
      console.error(error);
    }
  };

  const emailPattern =
    /^(?:"[a-zA-Z0-9!#$%&'*+/=?^_`{|}~\s-]+"|[a-zA-Z0-9!#$%&'*+/=?^_`{|}~](?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~\-]*[a-zA-Z0-9!#$%&'*+/=?^_`{|}~])?)(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~\-](?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~\-]*[a-zA-Z0-9!#$%&'*+/=?^_`{|}~])?)*@[a-zA-Z0-9-]+(?:\.[a-zA-Z]{2,})+$/;

  return (
    <>
      <PageHeader>
        <div className="flex flex-col" style={{ marginBottom: '30px' }}>
          <div id="leftTitle" style={{ display: 'inline', float: 'left' }}>
            <Title level={3} style={{ display: 'inline' }}>
              Report Cover Letter
            </Title>
          </div>
        </div>
      </PageHeader>
      <div className="flex flex-col">
        <div className="flex justify-between items-center">
          <Form
            form={form}
            onFinish={(values) => {
              onSubmit(values);
            }}
            onValuesChange={handleValuesChange}
            onFinishFailed={(err) => {
              console.log('err', err);
            }}
            layout="vertical"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'right',
              justifyContent: 'end'
            }}
          >
            <Row gutter={15}>
              <Col xs={24} sm={10}>
                <Form.Item label="Issued To" name="issuedTo" rules={[{ required: true, message: 'Issued To is required' }]}>
                  <Select style={{ width: '100%' }} placeholder="Select an institution">
                    {props.sendDestinationEntities
                      .filter((entity) => entity.isClient)
                      .map((entity) => (
                        <Select.Option key={entity.id} value={entity.entityName}>
                          {entity.entityName}
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="Inclusions" name="inclusions">
              <Row gutter={[10, 10]} style={{ marginTop: '10px' }}>
                {props.files.map((file, index) => (
                  <Col key={file}>
                    <Checkbox value={file} onChange={(e) => handleCheck(file, e.target.checked)} checked={selectedFiles.includes(file)} disabled={index === 0}>
                      {file}
                    </Checkbox>
                  </Col>
                ))}
              </Row>
            </Form.Item>
            <Title level={4} style={{ display: 'inline' }}>
              Candidate Details
            </Title>

            <Row gutter={20}>
              <Col xs={24} sm={12}>
                <Form.Item label="First Name" name="firstName" rules={[{ required: true, message: 'First Name is required' }]}>
                  <Input placeholder="Enter First Name" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item label="Last Name" name="lastName" rules={[{ required: true, message: 'Last Name is required' }]}>
                  <Input placeholder="Enter Last Name" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={20}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Date Of Birth"
                  style={{ width: '100%' }}
                  name="dateOfBirth"
                  valuePropName="value"
                  rules={[{ required: true, message: 'Date Of Birth is Required' }]}
                  getValueProps={(value) => ({
                    value: value ? dayjs(value) : ''
                  })}
                >
                  <DatePicker maxDate={dayjs().endOf('day')} format={(value) => value.format(DATE_FORMAT).toUpperCase()} style={{ width: isMobile ? '100%' : 'auto' }} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item name="gender" rules={[{ required: true, message: 'Gender is required' }]} label="Gender">
                  <Select style={{ width: isMobile ? '100%' : 100 }} placeholder="Gender">
                    {GENDERS.map((item) => {
                      return (
                        <Select.Option key={item.value} value={item.value} style={{ width: isMobile ? '100%' : 100, minHeight: '40px', padding: '8px' }}>
                          {item.label}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={20}>
              <Col xs={24} sm={12}>
                <Form.Item label="Applicant ID" name="applicantId" rules={[{ min: 24, message: 'Applicant ID must be at least 24 characters long' }]}>
                  <Input placeholder="Enter Applicant ID" disabled={applicantIdDisabled} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item label="Case ID" name="caseId" rules={[{ min: 24, message: 'Case ID must be at least 24 characters long' }]}>
                  <Input placeholder="Enter Case ID" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={20}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Email Address"
                  name="email"
                  rules={[{ pattern: emailPattern, message: 'Email must contain alphanumeric characters followed by "@" and a valid domain' }]}
                >
                  <Input placeholder="Email Address" disabled={emailDisabled} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label="Generational Suffix" name="generationalSuffix">
                  <Select
                    allowClear={true}
                    placeholder="Generational Suffix"
                    options={props.generationalSuffixOptions}
                    fieldNames={{ label: 'suffix', value: 'suffix' }}
                    style={{ width: isMobile ? '180%' : 'auto' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Title level={4} style={{ display: 'inline' }}>
              Credential Details
            </Title>
            <Row gutter={20}>
              <Col xs={24} sm={12}>
                <Form.Item label="Name on Document" name="nameOnDocument" rules={[{ required: true, message: 'Name on Document is required' }]}>
                  <Input placeholder="Enter Name on Document" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item label="Type Of Credential" name="typeOfCredential" rules={[{ required: true, message: 'Type of Credential is required' }]}>
                  <Input placeholder="Enter Type of Credential" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={20}>
              <Col xs={24} sm={12}>
                <Form.Item label="Status of Credential" name="statusOfCredential" rules={[{ required: true, message: 'Status of Credential is required' }]}>
                  <Input placeholder="Enter Status of Credential" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item label="Issuing Institution" name="issuingInstitution" rules={[{ required: true, message: 'Issuing Institution is required' }]}>
                  <Select style={{ width: '100%' }} placeholder="Select an Institution">
                    {props.issuingInstitutionEntities
                      .filter((entities) => entities.isIssuingInstitution)
                      .map((isIssuingInstitution) => (
                        <Select.Option key={isIssuingInstitution.id} value={isIssuingInstitution.entityName}>
                          {isIssuingInstitution.entityName}
                        </Select.Option>
                      ))}
                    <Select.Option key={'other'} value={'other'}>
                      Other
                    </Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {isOtherInstitution && (
              <>

              <Row gutter={20}>
                  <Col xs={24} sm={24}>
                    <Form.Item
                      label="Issuing Institution Name"
                      name="issuingInstitutionName"
                      rules={[{ required: true, message: 'Issuing Institution Name is required' }]}
                    >
                      <Input placeholder="Enter Issuing Institution Name" />
                    </Form.Item>
                  </Col>
                </Row>

              </>
            )}

            <Row gutter={20}>
              <Col xs={24} sm={11}>
                <PhotoUpload name="photo" label="Photo" form={form} cropperProps={{ aspect: 1 }} />
              </Col>
            </Row>
            <Row gutter={20}>
              <Col xs={24} sm={50}>
                <Form.Item>
                  <Button type="primary" htmlType="submit" style={{ width: '100%' }}>
                    Generate Report Cover Letter
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
        <div>
          <Modal open={isModalVisible} footer={null} onCancel={handleModalClose} width="80%" height="150%" style={{ top: 20 }} 
            bodyProps={{
              style: {paddingTop: MODAL_BODY_PADDING_TOP}
            }}
          >
            <embed src={pdfUrl ?? undefined} type="application/pdf" width="100%" height="1000px" title="Generated PDF" />
          </Modal>
        </div>
      </div>
    </>
  );
};
